/**
 * 🎡 RotationPoolAdmin — Owner-only curated pool manager for the House
 * Auction rotation. Toggle which items The Auctioneer is allowed to list,
 * with live counts + a manual "Refill Now" + "Clear Live" action.
 *
 * Endpoints:
 *   GET  /api/auction-house/rotation/pool
 *   POST /api/auction-house/rotation/pool/add     { item_id }
 *   POST /api/auction-house/rotation/pool/remove  { item_id }
 *   POST /api/auction-house/rotation/refill-now
 *   POST /api/auction-house/rotation/clear
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RARITY_COLOR = {
  common:    "border-neutral-500 text-neutral-200",
  uncommon:  "border-emerald-600 text-emerald-200",
  rare:      "border-blue-600 text-blue-200",
  epic:      "border-purple-600 text-purple-200",
  legendary: "border-amber-500 text-amber-200",
  mythic:    "border-pink-500 text-pink-200",
};

export default function RotationPoolAdmin() {
  return (
    <div className="space-y-4">
      <BuybackAdminPanel />
      <RotationPoolInner />
    </div>
  );
}

function RotationPoolInner() {
  const [snap, setSnap] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState({ rarity: "", pool: "all" });
  const [q, setQ] = useState("");
  const [quotas, setQuotas] = useState(null);
  const [quotasDirty, setQuotasDirty] = useState(false);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/auction-house/rotation/pool`);
      setSnap(r.data);
      if (!quotasDirty) {
        setQuotas(r.data.rarity_quotas || {});
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rotation snapshot failed.");
    }
  };

  useEffect(() => { refresh(); }, []);

  const toggle = async (item_id, cur_in_pool) => {
    setBusy(true);
    try {
      const path = cur_in_pool ? "remove" : "add";
      await axios.post(`${API}/auction-house/rotation/pool/${path}`, { item_id });
      toast.success(cur_in_pool ? "Removed from pool" : "Added to pool");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed.");
    }
    setBusy(false);
  };

  const refillNow = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auction-house/rotation/refill-now`);
      const n = (r.data?.listed || []).length;
      toast.success(n > 0 ? `🎡 Refilled ${n} listings.` : `Rotation already at floor (${r.data?.current}/${r.data?.target}).`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refill failed.");
    }
    setBusy(false);
  };

  const clearAll = async () => {
    if (!window.confirm("Force-expire all live House rotation listings? A fresh set will spawn on the next tick.")) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auction-house/rotation/clear`);
      toast.success(`Cleared ${r.data?.cleared} listings`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed.");
    }
    setBusy(false);
  };

  const saveQuotas = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/auction-house/rotation/quotas`, { quotas });
      toast.success("Rarity quotas saved.");
      setQuotasDirty(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save quotas failed.");
    }
    setBusy(false);
  };

  const resetQuotas = () => {
    if (snap?.default_quotas) {
      setQuotas({ ...snap.default_quotas });
      setQuotasDirty(true);
    }
  };

  const updateQuota = (rarity, value) => {
    const n = Math.max(0, Math.min(100, parseInt(value) || 0));
    setQuotas((q) => ({ ...(q || {}), [rarity]: n }));
    setQuotasDirty(true);
  };

  const filtered = useMemo(() => {
    if (!snap?.items) return [];
    return snap.items.filter((it) => {
      if (filter.rarity && it.rarity !== filter.rarity) return false;
      if (filter.pool === "in" && !it.in_pool) return false;
      if (filter.pool === "out" && it.in_pool) return false;
      if (q && !(it.name || "").toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [snap, filter, q]);

  if (!snap) {
    return (
      <div className="p-8 text-center text-neutral-500" data-testid="rotation-admin-loading">
        Loading rotation pool…
      </div>
    );
  }

  const rarities = Array.from(new Set((snap.items || []).map((i) => i.rarity).filter(Boolean)));

  return (
    <div data-testid="rotation-pool-admin" className="space-y-4">
      {/* Header */}
      <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-[#1a1305] via-black/40 to-[#0a0a05] p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-2xl font-black tracking-wide text-amber-300" style={{ fontFamily: "Anton, sans-serif" }}>
              🎡 REVOLVING ROTATION — CURATION
            </div>
            <div className="text-[11px] text-neutral-400 mt-1 max-w-xl leading-relaxed">
              Toggle items into the curated pool. When curated pool is non-empty, only those items rotate.
              Empty pool = every tradeable non-soulbound item is eligible (open mode).
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Pill icon="📦" label="Eligible" value={snap.eligible_size} testid="rot-eligible" />
            <Pill icon="✅" label="Pool" value={snap.pool_size} testid="rot-pool-size" />
            <Pill icon="🏛️" label="Live" value={`${snap.active_count}/${snap.target_floor}`} testid="rot-active" />
            <Pill icon="🌐" label="Mode" value={snap.pool_active_mode.toUpperCase()} testid="rot-mode" />
          </div>
        </div>
        <div className="mt-3 flex gap-2 flex-wrap">
          <button
            onClick={refillNow}
            disabled={busy}
            data-testid="rot-refill-now-btn"
            className="px-3 py-1.5 rounded-md text-xs font-bold bg-amber-500 hover:bg-amber-400 text-black disabled:opacity-40"
          >
            🚀 Refill Now
          </button>
          <button
            onClick={clearAll}
            disabled={busy}
            data-testid="rot-clear-btn"
            className="px-3 py-1.5 rounded-md text-xs font-bold bg-red-500/70 hover:bg-red-500 text-white disabled:opacity-40"
          >
            💥 Clear Live Listings
          </button>
          <button
            onClick={refresh}
            disabled={busy}
            data-testid="rot-refresh-btn"
            className="px-3 py-1.5 rounded-md text-xs font-bold bg-neutral-800 hover:bg-neutral-700 text-neutral-200 border border-neutral-600 disabled:opacity-40"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Rarity Quotas panel */}
      {quotas && (
        <div
          className="rounded-2xl border border-purple-500/30 bg-gradient-to-br from-[#100022] via-black/40 to-[#22001A] p-4"
          data-testid="rarity-quotas-panel"
        >
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div>
              <div className="text-lg font-black text-purple-200" style={{ fontFamily: "Anton, sans-serif" }}>
                🎯 RARITY QUOTAS
              </div>
              <div className="text-[11px] text-neutral-400 max-w-xl">
                Weighted picks per rarity. Higher weight = more likely to be listed.
                Set 0 to exclude a rarity entirely. Applies to <b>OPEN</b> and <b>CURATED</b> modes.
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={saveQuotas}
                disabled={busy || !quotasDirty}
                data-testid="rot-quotas-save"
                className="px-3 py-1.5 rounded-md text-xs font-bold bg-purple-500 hover:bg-purple-400 text-white disabled:opacity-40"
              >
                💾 Save Quotas
              </button>
              <button
                onClick={resetQuotas}
                disabled={busy}
                data-testid="rot-quotas-reset"
                className="px-3 py-1.5 rounded-md text-xs font-bold bg-neutral-800 hover:bg-neutral-700 text-neutral-200 border border-neutral-600 disabled:opacity-40"
              >
                ↺ Defaults
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {Object.entries(quotas).map(([rarity, weight]) => (
              <div
                key={rarity}
                className="rounded-lg border border-purple-500/30 bg-black/40 p-2"
                data-testid={`rot-quota-${rarity}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-[11px] font-bold uppercase tracking-widest ${RARITY_COLOR[rarity]?.split(' ').find(c => c.startsWith('text-')) || 'text-neutral-300'}`}>
                    {rarity}
                  </span>
                  <span className="text-sm font-black text-purple-200" data-testid={`rot-quota-value-${rarity}`}>
                    {weight}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={weight}
                  onChange={(e) => updateQuota(rarity, e.target.value)}
                  data-testid={`rot-quota-slider-${rarity}`}
                  className="w-full accent-purple-400"
                />
              </div>
            ))}
          </div>
          {quotasDirty && (
            <div className="mt-2 text-[10px] text-amber-300" data-testid="rot-quotas-dirty">
              ⚠️ Unsaved changes — click Save.
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search item name…"
          className="px-3 py-1.5 rounded-md text-xs bg-black/60 border border-neutral-700 text-neutral-100 min-w-[180px]"
          data-testid="rot-search-input"
        />
        <select
          value={filter.rarity}
          onChange={(e) => setFilter((f) => ({ ...f, rarity: e.target.value }))}
          className="px-3 py-1.5 rounded-md text-xs bg-black/60 border border-neutral-700 text-neutral-100"
          data-testid="rot-filter-rarity"
        >
          <option value="">All rarities</option>
          {rarities.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        <select
          value={filter.pool}
          onChange={(e) => setFilter((f) => ({ ...f, pool: e.target.value }))}
          className="px-3 py-1.5 rounded-md text-xs bg-black/60 border border-neutral-700 text-neutral-100"
          data-testid="rot-filter-pool"
        >
          <option value="all">All items</option>
          <option value="in">In pool only</option>
          <option value="out">Not in pool</option>
        </select>
        <span className="text-[10px] text-neutral-500 ml-1">
          Showing {filtered.length} / {snap.items?.length || 0}
        </span>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="rot-item-grid">
        {filtered.map((it) => (
          <ItemCard
            key={it.item_id}
            item={it}
            busy={busy}
            onToggle={() => toggle(it.item_id, it.in_pool)}
          />
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-neutral-500 text-xs p-6" data-testid="rot-empty">
            No items match your filter.
          </div>
        )}
      </div>
    </div>
  );
}

function Pill({ icon, label, value, testid }) {
  return (
    <div className="px-2.5 py-1 rounded-full text-[10px] bg-amber-500/15 border border-amber-400/40 text-amber-200 font-semibold flex items-center gap-1" data-testid={testid}>
      <span>{icon}</span><span className="text-neutral-400">{label}:</span> <b>{value}</b>
    </div>
  );
}

function ItemCard({ item, busy, onToggle }) {
  const rarityStyle = RARITY_COLOR[item.rarity] || "border-neutral-700 text-neutral-300";
  return (
    <div
      className={`rounded-lg border p-2.5 ${rarityStyle} ${item.in_pool ? "bg-amber-500/10 ring-1 ring-amber-400/40" : "bg-neutral-900/50"}`}
      data-testid={`rot-item-${item.item_id}`}
      data-in-pool={item.in_pool ? "1" : "0"}
      data-live={item.live_now ? "1" : "0"}
    >
      <div className="flex items-start gap-2">
        <div className="text-2xl">{item.icon || "📦"}</div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-neutral-100 truncate" title={item.name}>
            {item.name}
          </div>
          <div className="text-[9px] text-neutral-500 uppercase tracking-widest mt-0.5">
            {item.slot} · {item.rarity}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between mt-2 gap-1">
        <div className="text-[10px] text-neutral-400">
          {item.live_now && <span className="text-emerald-300">● LIVE</span>}
          {!item.live_now && <span>~{item.start_price}🪙</span>}
        </div>
        <button
          onClick={onToggle}
          disabled={busy}
          data-testid={`rot-toggle-${item.item_id}`}
          className={
            "px-2 py-0.5 rounded text-[10px] font-bold disabled:opacity-40 " +
            (item.in_pool
              ? "bg-red-500/70 hover:bg-red-500 text-white"
              : "bg-amber-500 hover:bg-amber-400 text-black")
          }
        >
          {item.in_pool ? "Remove" : "Add"}
        </button>
      </div>
    </div>
  );
}

/* ─── 🏛️ Buyback Admin Panel ────────────────────────────────────── */
function BuybackAdminPanel() {
  const [cfg, setCfg] = useState(null);
  const [ratios, setRatios] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/auction-house/buyback/config`);
      setCfg(r.data);
      if (!dirty) setRatios({ ...(r.data.ratios || {}) });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Buyback config load failed.");
    }
  };
  useEffect(() => { refresh(); }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auction-house/buyback/toggle`, {
        enabled: !cfg.enabled,
      });
      setCfg(r.data);
      toast.success(`Buy-back ${r.data.enabled ? "ENABLED" : "DISABLED"}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed.");
    }
    setBusy(false);
  };

  const updateRatio = (rarity, val) => {
    const pct = Math.max(0, Math.min(200, parseInt(val) || 0));
    setRatios((r) => ({ ...(r || {}), [rarity]: pct / 100 }));
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auction-house/buyback/ratios`, { ratios });
      setCfg(r.data);
      setRatios({ ...(r.data.ratios || {}) });
      setDirty(false);
      toast.success("Buy-back ratios saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
    setBusy(false);
  };

  const reset = () => {
    if (cfg?.defaults) {
      setRatios({ ...cfg.defaults });
      setDirty(true);
    }
  };

  if (!cfg || !ratios) {
    return (
      <div className="p-6 text-center text-neutral-500 text-xs" data-testid="buyback-admin-loading">
        Loading buy-back config…
      </div>
    );
  }

  const rarities = Object.keys(cfg.defaults || {});

  return (
    <div
      className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-[#001a10] via-black/40 to-[#001A22] p-4"
      data-testid="buyback-admin-panel"
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-lg font-black text-emerald-200" style={{ fontFamily: "Anton, sans-serif" }}>
            🏛️ AUCTIONEER BUY-BACK
          </div>
          <div className="text-[11px] text-neutral-400 max-w-xl">
            Global toggle + per-rarity payout ratios. 100% = full House price refund.
            Values persist until you change them — no scheduler resets.
          </div>
        </div>
        <button
          onClick={toggle}
          disabled={busy}
          data-testid="buyback-toggle-btn"
          className={
            "px-4 py-2 rounded-lg text-xs font-black transition-all " +
            (cfg.enabled
              ? "bg-emerald-500 hover:bg-emerald-400 text-black"
              : "bg-red-600 hover:bg-red-500 text-white")
          }
        >
          {cfg.enabled ? "✅ BUY-BACK: ON" : "🚫 BUY-BACK: OFF"}
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-3">
        {rarities.map((r) => (
          <div
            key={r}
            className="rounded-lg border border-neutral-700 bg-black/40 p-2"
            data-testid={`buyback-ratio-row-${r}`}
          >
            <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1">{r}</div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                max="200"
                value={Math.round((ratios[r] ?? 0.5) * 100)}
                onChange={(e) => updateRatio(r, e.target.value)}
                className="w-16 px-2 py-1 rounded bg-black/70 border border-neutral-600 text-center text-emerald-100 text-sm"
                data-testid={`buyback-ratio-input-${r}`}
              />
              <span className="text-xs text-neutral-400">%</span>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={save}
          disabled={busy || !dirty}
          data-testid="buyback-save-btn"
          className="px-3 py-1.5 rounded-md text-xs font-bold bg-emerald-500 hover:bg-emerald-400 text-black disabled:opacity-40"
        >
          💾 Save Ratios
        </button>
        <button
          onClick={reset}
          disabled={busy}
          data-testid="buyback-reset-btn"
          className="px-3 py-1.5 rounded-md text-xs font-bold bg-neutral-800 hover:bg-neutral-700 text-neutral-200 border border-neutral-600 disabled:opacity-40"
        >
          ↺ Reset Defaults (50%)
        </button>
        {dirty && <span className="text-[10px] text-amber-300">unsaved changes</span>}
      </div>
    </div>
  );
}

